/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2018 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property of
 * Pearson Education, Inc. The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S.
 * and Foreign Patents, patent applications, and are protected by trade secret
 * or copyright law. Dissemination of this information, reproduction of this
 * material, and copying or distribution of this software is strictly forbidden
 * unless prior written permission is obtained from Pearson Education, Inc.
 */

import React, {Component} from 'react';
import config from "../../../config";
import urlHelper from "../../../_helpers/url.format.server.helper";
import {Link, Prompt} from 'react-router-dom';
import BookDetails from "./BookDetails";
import OrderDetails from "./OrderDetails";
import BundleInfo from "./BundleInfo";
import BookComponents from "./BookComponents";
import PropTypes from 'prop-types';
import CoverTitle from "./CoverTitle";
import {CollectionConsumer, CollectionProvider} from "../../context/CollectionContext";
import BookNavigationPanel from "./BookContainerComponents/BookNavigationPanel";
import PdfLink from "./BookContainerComponents/PdfLink";
import { StaticAlert } from '@pearson-components/elements-sdk/build/dist.elements-sdk';
import AxiosClient from "../../../_helpers/http.helper";
import {PCSLogComponent} from "./BookContainerComponents/PCSLogComponent";
import {Redirect, Route, Switch} from "react-router";
import guardDirtyState from 'react-router-guard-dirty-state';
import {PAPER_TYPES, BINDING_TYPES, PAPER_WEIGHTS, PERMISSIONS } from "../../constants/constants";
import {AuthConsumer} from "../../context/AuthContext";
import moment from 'moment';
import CollectionAlerts from './BookContainerComponents/CollectionAlerts';
import { AlertConsumer } from '../../context/AlertContext';
import { asyncWrapper } from '../../../_helpers/async.helper';
import { TEXT_TYPES } from "../../../constants/APP_CONSTANTS";
import AddNoteModal from "./BookDetailsComponents/AddNoteModal";
import ModalDialog from "../../widgets/ModalDialog/ModalDialog";

class BookContainerWithContext extends Component {
    static defaultProps = {
        guardDirtyIsActive: false,

    };

    static get propTypes() {
        return {
            loadBookBuildDetails: PropTypes.func.isRequired,
            collection: PropTypes.object.isRequired,
            bookBuildDetails: PropTypes.object.isRequired,
            loadInitialData: PropTypes.func.isRequired,
            updateCollection: PropTypes.func.isRequired,
            updateBookBuildDetails: PropTypes.func.isRequired,
            guardDirtySetActive: PropTypes.func.isRequired,
            guardDirtyIsActive: PropTypes.bool.isRequired,
            resourceSharing: PropTypes.bool.isRequired,
            addAlert: PropTypes.func.isRequired,
        };
    }

    constructor(props){
        super(props);
        this.state = {
            type: "",
            message: "",
            pcsLogs: [],
            paperTypes: null,
            bindingTypes: null,
            selectedBindingType: null,
            selectedPaperType: null,
            bookDetails: {
                automaticPageNumbering: false,
                automaticIndexes: false,
                forcedRectoStart: false,
                printColorSetting: "",
                printTOCChapterType: "",
                paperWeight: null,
                title: null,
                pageCount: 0
            },
            collectionNotes: null,
            currentComponent: "",
            deskCopies: {},
            pdfs: [],
            totalPdfs: [],
            frontMatterFilteredPdfs: [],
            coverAndTitlePage: {},
            bundleInfo: [],
            isChangesValid: true,
            newNote: {
                credit : false,
                rights : false,
                cover : false,
                other : false,
                note : ''
            },
            isNotesModalActive: false,
            pitStopStatus: {}
        };
    }

    componentDidMount(){
        this.getBookBuildDetails(this.props.match.params.collId);
        this.getDeskCopies(this.props.match.params.collId)
            .then(response => {
                this.setState({
                    deskCopies: response.data
                });
            });
        this.fetchPaperTypes();
        this.fetchBindingTypes();
        this.fetchNotes();
        this.loadBundleInfo(this.props.match.params.collId);
    }

    componentWillUpdate(nextProps, nextState, nextContext) {
        if (nextProps.deskCopies !== this.props.deskCopies) {
            this.getDeskCopies(this.props.match.params.collId)
                .then(response => {
                    this.setState({
                        deskCopies: response.data
                    });
                });
        }

        if(nextProps.collection.paperWeight && this.state.bookDetails.paperWeight
            !== nextProps.collection.paperWeight.specificationDetail.name) {
            this.setState({
                bookDetails:{...this.state.bookDetails,
                    paperWeight: nextProps.collection.paperWeight.specificationDetail.name
                }
            });
        }

        if(nextProps.collection && this.state.bookDetails.title
            !== nextProps.collection.title) {
            this.setState({
                bookDetails:{...this.state.bookDetails,
                    title: nextProps.collection.title
                }
            });
        }
    }

    componentDidUpdate = (prevProps, prevState) => {
        if (this.state.currentComponent !== prevState.currentComponent
            || this.state.bundleInfo !== prevState.bundleInfo) {
            this.validateChanges();
        }
    }
    setFormDirty = (isDirty = false) => {
        if (typeof this.props.guardDirtySetActive === 'function') {
            this.props.guardDirtySetActive(isDirty);
        }
    };

    getDeskCopies = (collectionId) => {
        const url = urlHelper.formatUrl(
            config.collectionService.baseUrl,
            config.collectionService.routes.deskcopy,{
                collectionId: collectionId
            });

        return AxiosClient.get(url);
    };

    setActiveComponent = (component) =>{
        this.setState({
            currentComponent: component
        });
    };

    rebuildBook = () => {
        this.rebuildBookSetup(this.props.match.params.collId);
        this.getBookBuildDetails(this.props.match.params.collId).then(() => {
            this.fetchNotes();
        });
    };

    getPdfLinks = () => {
        const url = urlHelper.formatUrl(
            config.bookBuildService.baseUrl,
            config.bookBuildService.routes.pdfs,
            {
                collectionId: this.props.match.params.collId
            }
        );
        AxiosClient.get(url)
            .then(response => {
                this.setState({ frontMatterFilteredPdfs: this.getFilteredPdfs(JSON.parse(JSON.stringify(response.data))) });
                this.setState({ totalPdfs: JSON.parse(JSON.stringify(response.data)) });
                this.setState({ pdfs: this.filterPdfs(response.data) });
            })
            .catch(error => {
                console.log("Error ", error);
            });
    };

    getFilteredPdfs = (pdfs) => {
        let allowedPdfTypes = ["CopyrightPage", "TitlePage", "TOCPage"];
        if (pdfs) {
            if (pdfs.filter(pdf => TEXT_TYPES.CUSTOM_TOC === pdf.printPageType).length > 0) {
                allowedPdfTypes[2] = TEXT_TYPES.CUSTOM_TOC;
            }
            if (pdfs.filter(pdf => TEXT_TYPES.CUSTOM_TITLE === pdf.printPageType).length > 0) {
                allowedPdfTypes[1] = TEXT_TYPES.CUSTOM_TITLE;
            }
            if (pdfs.filter(pdf => TEXT_TYPES.CUSTOM_COPYRIGHT === pdf.printPageType).length > 0) {
                allowedPdfTypes[0] = TEXT_TYPES.CUSTOM_COPYRIGHT;
            }
            if (pdfs.filter(pdf => TEXT_TYPES.CUSTOM_FONTMATTER === pdf.printPageType).length > 0) {
                allowedPdfTypes.push(TEXT_TYPES.CUSTOM_FONTMATTER);
            }
            let filteredPrintPageType = [];
            const filteredPdfs = [...pdfs]
                .sort((first, second) => second.createDate - first.createDate)
                .filter(pdf => {
                    if(allowedPdfTypes.includes(pdf.printPageType) && pdf.isEPSUploaded === true && !filteredPrintPageType.includes(pdf.printPageType)){
                        filteredPrintPageType.push(pdf.printPageType);
                        return true;
                    }
                });
            return filteredPdfs;
        }
    }

    filterPdfs = (data) => {
        const allowedPdfTypes = ["MergedCoverPageWithBarCode", "FrontMatter", "TOCPage", "BookContent", "IndexPage", "PrintBook"];
        const pdfDisplayNames = ["Cover", "Front Matter", "Table of Contents", "Content", "Index", "Full Book Content"];
        const customPdfTypes = [];
        let filteredPdfs = [];

        data.map(printContent => {
            if (printContent.contentSource === "Custom") {
                printContent.printPageType = printContent.printPageType.replace("Custom", "");
                customPdfTypes.push(printContent.printPageType);
            }
            return printContent;
        })
            .filter(pdf => allowedPdfTypes.includes(pdf.printPageType) && (pdf.contentSource === "Custom" || !customPdfTypes.includes(pdf.printPageType)))
            .sort((pdf1, pdf2) => pdf2.lastModified - pdf1.lastModified)
            .forEach((pdf) => {
                if (!filteredPdfs.find((item) => item.printPageType === pdf.printPageType)) {
                    filteredPdfs.push(pdf);
                }
            });
        filteredPdfs.sort((pdf1, pdf2) => {
            const pdf1Index = allowedPdfTypes.findIndex((item) => item === pdf1.printPageType);
            const pdf2Index = allowedPdfTypes.findIndex((item) => item === pdf2.printPageType);
            return pdf1Index - pdf2Index;
        });
        filteredPdfs = filteredPdfs.map(pdf => {
            const index = allowedPdfTypes.findIndex((item) => item === pdf.printPageType);
            pdf.printPageType = pdfDisplayNames[index];
            return pdf;
        });
        return filteredPdfs;
    };

    fetchNotes = () => {
        const url = urlHelper.formatUrl(
            config.collectionService.baseUrl,
            config.collectionService.routes.getNotes,
            {
                collectionId: this.props.match.params.collId
            }
        );
        AxiosClient.get(url)
            .then(response => {
                this.setState({
                    collectionNotes: response.data
                });
            })
            .catch(error => {
                console.log("Error ", error);
            });
    };

    rebuildBookSetup = (collectionId) => {
        const rebuildUrl = urlHelper.formatUrl(
            config.bookBuildService.baseUrl,
            config.bookBuildService.routes.rebuildBook,
            {
                collectionId: collectionId
            }
        );
        AxiosClient.post(rebuildUrl)
            .then(response => {
                this.getPdfLinks();
                this.props.addAlert("Book rebuild started.",  "Success", "Success");
            })
            .catch(error => {
                this.props.addAlert("Collection Rebuild in progress. Please wait till the rebuild collection completes.",  "Error", "Error");
            });
    };

    fetchPCSLogs = () => {
        const url = urlHelper.formatUrl(
            config.collectionService.baseUrl,
            config.collectionService.routes.getPCSLogs,
            {
                collectionId: this.props.match.params.collId
            }
        );
        AxiosClient.get(url)
            .then(response => {
                this.setState(
                    {
                        pcsLogs: response.data
                    });
            });
    };

    getBookBuildDetails = (collectionId) => {

        const url = urlHelper.formatUrl(
            config.collectionService.baseUrl,
            config.collectionService.routes.getBookBuildContent,
            {
                collectionId: collectionId
            }
        );
        AxiosClient.get(url)
            .then(response => {
                return response.data;
            })
            .catch( error =>{
                console.log("Error", error);
            });
    };

    loadBundleInfo = (collectionId) => {
        const url = urlHelper.formatUrl(
            config.collectionService.baseUrl,
            config.collectionService.routes.getBundles,
            { collectionId }
        );

        return AxiosClient.get(url)
            .then(response => {
                return this.setState({
                    bundleInfo: [...response.data, {}]
                });
            })
            .catch( error =>{
                console.log("Error", error);
            });
    };

    sendToPCS = () => {
        const pcsRoute = urlHelper.formatUrl(config.collectionService.baseUrl,
            config.collectionService.routes.sendToPCS,
            {
                collectionId: this.props.match.params.collId,
                queryParams: {ac: false}
            });

        AxiosClient.post(pcsRoute)
            .then(() => {
                this.props.addAlert("Send To PDH Successful.",  "", "Success");
            })
            .catch(error => {
                this.props.addAlert("Send To PDH Failed!",  "", "Error");
            });
    };

    validateChanges = () => {
        let isValid = true;
        if (this.state.currentComponent === "BundleInfo") {
            if (this.state.bundleInfo.find(bundle => bundle.isInvalid)) {
                isValid = false;
            }
        }

        this.setState({ isChangesValid: isValid });
    }

    saveChanges = async () => {
        let result = {};
        if(this.state.currentComponent === 'OrderDetails'){
            result = await asyncWrapper(this.saveOrderDetails(this.props.match.params.collId));
        }else if(this.state.currentComponent === 'CoverTitle'){
            result = await asyncWrapper(this.saveCoverPageDetails());
        } else if (this.state.currentComponent === "BundleInfo") {
            result = await asyncWrapper(this.saveBundleInfo());
        }else if(this.state.currentComponent === 'BookDetails'){
            result = await asyncWrapper(this.saveBookDetailsPage(this.props.match.params.collId));
        }

        const { error, data } = result;
        if (error) {
            this.props.addAlert("Your changes were not saved.",  "Error occurred!", "Error");
        } else if (data && data.showCustomMessage) {
            this.props.addAlert(data.message,  data.title, data.type);
        } else {
            this.props.addAlert("Successfully saved.",  "Success", "Success");
        }

        this.setFormDirty(false);
    };

    saveOrderDetails =  async (collectionId) => {
        await this.updateSalesForceOrgUnit(collectionId);
        await this.saveAdopterInfo(collectionId);
        await this.saveCourseInfo(collectionId);
        await this.saveSchoolTerm(collectionId);
        await this.saveDeskCopyInfo(collectionId).then(() => {
            this.getDeskCopies(collectionId);
        });
        await this.props.loadInitialData(collectionId);
    };

    saveBookDetailsPage = async (collectionId) => {
        await this.saveBookDetails(collectionId);
        await this.savePaperType(collectionId);
    }

    saveAdopterInfo = (collectionId) => {
        const url = urlHelper.formatUrl(
            config.collectionService.baseUrl,
            config.collectionService.routes.adopterSave,{
                collectionId: collectionId,
                queryParams: {
                    firstName: this.props.bookBuildDetails.adopterFirstName,
                    lastName: this.props.bookBuildDetails.adopterLastName
                }
            });
        return AxiosClient.put(url);
    };

    saveSchoolTerm = (collectionId) => {
        const url = urlHelper.formatUrl(
            config.collectionService.baseUrl,
            config.collectionService.routes.saveSchoolTerm,{
                collectionId: collectionId
            });
        const schoolTerm = this.props.bookBuildDetails.schoolTerm;
        return AxiosClient.put(url,{schoolTerm});
    };

    saveDeskCopyInfo = (collectionId) => {
        const url = urlHelper.formatUrl(
            config.collectionService.baseUrl,
            config.collectionService.routes.deskcopy,{
                collectionId: collectionId
            });

        return AxiosClient.put(url, this.state.deskCopies);
    };

    saveCourseInfo = (collectionId) => {
        const url = urlHelper.formatUrl(
            config.collectionService.baseUrl,
            config.collectionService.routes.courseinfo,{
                collectionId: collectionId
            });
        return AxiosClient.patch(url,this.props.collection.collectionCourseInformation)
            .then((response) => {
                return response;
            })
            .catch(error => {

            });
    };

    updateSalesForceOrgUnit = (collectionId) => {
        const url = urlHelper.formatUrl(
            config.collectionService.baseUrl,
            config.collectionService.routes.salesForce,{
                collectionId: collectionId,
                queryParams: {
                    orgUnit: this.props.bookBuildDetails.salesForceOrg
                }
            });
        return AxiosClient.post(url);
    };

    updateDeskCopy = (field, value) => {
        this.setState({
            deskCopies: {
                ...this.state.deskCopies,
                [field]: value
            }
        });
    };

    changeBookTitle = (value => {

        let coverAndTitleDetails = {...this.state.coverAndTitlePage};
        coverAndTitleDetails.titlePage.title = value;

        this.setState({
            coverAndTitlePage: coverAndTitleDetails
        });

    });

    changeBookSubtitle = (value => {

        let coverAndTitleDetails = {...this.state.coverAndTitlePage};
        coverAndTitleDetails.titlePage.subTitle = value;

        this.setState({
            coverAndTitlePage: coverAndTitleDetails
        });

    });

    changeBookAuthor = (value => {

        let coverAndTitleDetails = {...this.state.coverAndTitlePage};
        coverAndTitleDetails.titlePage.authorName = value;

        this.setState({
            coverAndTitlePage: coverAndTitleDetails
        });

    });

    changeBookOther = (value => {

        let coverAndTitleDetails = {...this.state.coverAndTitlePage};
        coverAndTitleDetails.titlePage.content = value;

        this.setState({
            coverAndTitlePage: coverAndTitleDetails
        });

    });

    setCoverAndTitlePageState = (value => {

        this.setState({
            coverAndTitlePage:value
        });

    });

    saveCoverPageDetails = async () => {

        const url = urlHelper.formatUrl(
            config.collectionService.baseUrl,
            config.collectionService.routes.titlePageInfo,
            {
                collectionId: this.props.match.params.collId
            }
        );

        if(this.state.coverAndTitlePage.titlePage.title === ""){
            return ({
                showCustomMessage: true,
                type: "Error",
                title: "Error occurred",
                message: "Cover title required."
            });
        } else {
            return AxiosClient.post(url,this.state.coverAndTitlePage.titlePage);
        }

    };

    fetchBindingTypes = () => {
        const url = urlHelper.formatUrl(
            config.collectionService.baseUrl,
            config.collectionService.routes.bindingTypes,
            {
                collectionId: this.props.match.params.collId
            }
        );
        AxiosClient.get(url)
            .then(response => {
                this.setState({
                    bindingTypes: response.data,
                });
                Object.keys(response.data).forEach((key) => {
                    if(response.data[key].selected) {
                        this.setState({
                            selectedBindingType: response.data[key].specificationDetail.name
                        });
                    }
                });

            })
            .catch(error => {
                console.log("Error ", error);
            });
    };

    fetchPaperTypes = () => {
        const url = urlHelper.formatUrl(
            config.collectionService.baseUrl,
            config.collectionService.routes.paperTypes,
            {
                collectionId: this.props.match.params.collId
            }
        );
        AxiosClient.get(url)
            .then(response => {
                this.setState({
                    paperTypes: response.data,
                });
                Object.keys(response.data).forEach((key) => {
                    if(response.data[key].selected) {
                        this.setState({
                            selectedPaperType: response.data[key].specificationDetail.name
                        });
                    }
                });
            })
            .catch(error => {
                console.log("Error ", error);
            });
    };

    changeCollectionTitle = ((title, updateGuardDuty) => {

        this.props.updateCollection('title', title);
        updateGuardDuty ? this.props.guardDirtySetActive(true): '';
    });

    changePrintColorSetting = ((colour, updateGuardDuty) => {

        this.setState({
            bookDetails:{...this.state.bookDetails,
                printColorSetting: colour
            }
        }, () =>  updateGuardDuty?this.props.guardDirtySetActive(true):'');

    });


    changePrintTOCChapterType = ((type,updateGuardDuty) => {

        this.setState({
            bookDetails:{...this.state.bookDetails,
                printTOCChapterType: type
            }
        }, () =>  updateGuardDuty?this.props.guardDirtySetActive(true):'');

    });

    changeAutomaticPageNumbering = ((value,updateGuardDuty) => {

        this.setState({
            bookDetails:{...this.state.bookDetails,
                automaticPageNumbering: value
            }
        }, () =>  updateGuardDuty?this.props.guardDirtySetActive(true):'');

    });

    changeAutomaticIndexes = ((value, updateGuardDirty) => {

        this.setState({
            bookDetails:{...this.state.bookDetails,
                automaticIndexes: value
            }
        }, () => updateGuardDirty? this.props.guardDirtySetActive(true) :'');

    });

    changeForcedRectoStart = ((value,updateGuardDirty) => {

        this.setState({
            bookDetails:{...this.state.bookDetails,
                forcedRectoStart: value
            }
        }, () =>  updateGuardDirty?this.props.guardDirtySetActive(true):'');

    });

    changePaperWeight = ((value,updateGuardDirty) => {

        this.props.updateCollection('paperWeight', {'specificationDetail' : this.getPaperWeight(value)});
        updateGuardDirty?this.props.guardDirtySetActive(true):'';


    });

    changeSelectedBindingType = ((value,updateGuardDirty)=> {
        this.setState({
            selectedBindingType: value
        }, () => this.saveBindingType(this.props.match.params.collId));
    });

    changeSelectedPaperType = ((value,updateGuardDirty) => {

        this.setState({
            selectedPaperType: value
        }, () =>  updateGuardDirty?this.props.guardDirtySetActive(true):'');

    });

    syncPageCount = (value => {

        this.setState({
            bookDetails:{...this.state.bookDetails,
                pageCount: value
            }
        });


    });


    getSpecificationId(code, data){
        for (const item of data ? data : []) {
            if (item.specificationDetail.code === code){
                return ({
                    specificationDetail: {
                        id: item.specificationDetail.id
                    }
                });
            }
        }
    }

    getBindingOption(name){
        return BINDING_TYPES.filter(type => type.name === name)[0];
    }

    getPaperOption(name){
        return PAPER_TYPES.filter(type => type.name === name)[0];
    }

    getPaperWeight(name){
        return PAPER_WEIGHTS.filter(type => type.name === name)[0];
    }

    saveBookDetails = async (collectionId) => {
        const url = urlHelper.formatUrl(
            config.collectionService.baseUrl,
            config.collectionService.routes.saveBookDetails,
            {
                collectionId: collectionId
            }
        );
        return AxiosClient.put(url, this.state.bookDetails);
    };

    saveBindingType = async (collectionId) => {
        const url = urlHelper.formatUrl(
            config.collectionService.baseUrl,
            config.collectionService.routes.changeSpecificationType,
            {
                collectionId: collectionId,
                type: 'Binding'
            }
        );
        const bindingType = this.getBindingOption(this.state.selectedBindingType);
        return AxiosClient.put(url, this.getSpecificationId(bindingType.code, this.state.bindingTypes))
            .then(() => { this.fetchPaperTypes();})
        ;
    };

    savePaperType = async (collectionId) => {
        const url = urlHelper.formatUrl(
            config.collectionService.baseUrl,
            config.collectionService.routes.changeSpecificationType,
            {
                collectionId: collectionId,
                type: 'PaperType'
            }
        );


        const paperType = this.getPaperOption(this.state.selectedPaperType);

        return AxiosClient.put(url, this.getSpecificationId(paperType.code, this.state.paperTypes));
    };
    sendToPitStop = () => {
        let payload = [];
        payload.push(this.props.match.params.collId);
        const url = urlHelper.formatUrl(
            config.collectionService.baseUrl,
            config.collectionService.routes.sendToPitStop);
        AxiosClient.post(url,payload).then(() => {
            this.props.addAlert("Send to PitStop Action successfully performed.",  "Success!", "Success");
        }).catch(() => {
            this.props.addAlert("Send to PitStop Action unsuccessful.",  "Error!", "Error");
        });
    };

    cancelOrder = () => {
        const url = urlHelper.formatUrl(
            config.collectionService.baseUrl,
            config.collectionService.routes.cancelOrder,
            {
                collectionId: this.props.match.params.collId
            }
        );
        AxiosClient.post(url)
            .then(response => {
                this.props.addAlert("Book Action successfully performed.",  "Success!", "Success");
                this.props.loadInitialData(this.props.match.params.collId);
            })
            .catch(error => {
                this.props.addAlert("Error " + error.response.status + " ! Book Action unsuccessful.",  "", "Error");
            });
        this.showNoteModal();
    };
   
    handlePitStopStatus = (data) => {
        this.setState({pitStopStatus: data});
    }
    
    sendToPrinter = () => {
        const pitStopStatus = this.state.pitStopStatus;
        const printBook = this.state.pitStopStatus.PrintBook;
        const mergedCoverPageWithBarCode = this.state.pitStopStatus.MergedCoverPageWithBarCode;

        if ( printBook !== null
             && pitStopStatus !== null
             && pitStopStatus !== ''
             && mergedCoverPageWithBarCode !== null
             && printBook !== undefined 
             && printBook.preflightStatusType !== undefined
             && printBook.preflightStatusType !== ''
             && mergedCoverPageWithBarCode.preflightStatusType !== '' 
             && mergedCoverPageWithBarCode !== undefined 
             && printBook.preflightStatusType === 'PitstopPassed'
             && mergedCoverPageWithBarCode.preflightStatusType !== undefined
             && mergedCoverPageWithBarCode.preflightStatusType === 'PitstopPassed' ){
            const url = urlHelper.formatUrl(
                config.collectionService.baseUrl,
                config.collectionService.routes.sendToPrinter,
                {
                    collectionId: this.props.match.params.collId
                }
            );
            AxiosClient.defaults.headers.common['X-UserId'] = this.props.userId;

            AxiosClient.post(url)
                .then(response => {
                    this.props.addAlert("Book Action successfully performed.",  "Success!", "Success");
                })
                .catch(error => {
                    if (error.response.data.errorCode === "CL0836") {
                        this.props.addAlert("Your print job was placed on hold.",  "", "Error");
                    } else {
                        this.props.addAlert("Error " + error.response.status + " ! Book Action unsuccessful.",  "", "Error");
                    }
                });
        }
        else{
            this.props.addAlert("Error! Files must pass Pitstop before Send to Printer", "", "Error");
        }
    };
    saveBundleInfo = async () => {
        const url = urlHelper.formatUrl(
            config.collectionService.baseUrl,
            config.collectionService.routes.saveBundleInfo,
            {
                collectionId: this.props.match.params.collId
            }
        );

        const result = this.state.bundleInfo.filter(bundle => !!bundle.isbn);

        return AxiosClient.post(url, result)
            .then(() => {
                this.loadBundleInfo(this.props.match.params.collId);
            });
    }

    setBundleInfo = (bundleInfo) => {
        this.setState({ bundleInfo });
        this.setFormDirty(true);
    }

    showNoteModal = () => {
        this.setState({
            isNotesModalActive : true
        });
    };

    hideNoteModal = () => {
        this.loadCurrentHoldsStatus();
        this.setState({
            isNotesModalActive : false
        });
    };

    loadCurrentHoldsStatus = () => {
        const nextStateNotes = {
            credit : false,
            rights : false,
            cover : false,
            other : false,
            note : ''
        };
        this.state.collectionNotes.holds.forEach(hold => {
            nextStateNotes[hold.toLowerCase()] = true;
        });

        this.setState({
            newNote: nextStateNotes
        });

    };

    toggleHold = (hold) => {
        const holdName = hold.toLowerCase();
        this.setState(prevState => {
            const newNote = prevState.newNote;
            newNote[holdName] = !newNote[holdName];
            return newNote;
        });
    };

    updateNote = (newNote) => {
        this.setState(prevState => ({
            newNote: {
                ...prevState.newNote,
                note : newNote
            }
        }));
    };


    saveNotesWithHolds = () => {
        if(this.state.newNote.note.length < 1){
            return alert("Please enter a note.");
        }

        this.setState({
            isNotesModalActive : false
        });

        const url = urlHelper.formatUrl(
            config.collectionService.baseUrl,
            config.collectionService.routes.saveNotesWithHolds,
        );

        const collectionNotes =  {
            body : this.state.newNote.note,
            collection:  this.state.collectionNotes &&  this.state.collectionNotes.length > 0 ?  this.state.collectionNotes[0].collection : this.props.collection,
            collectionNoteHolds: [],
        };

        const changeStatus = {
            Credit: this.state.newNote.credit !==  this.state.collectionNotes.holds.includes('Credit'),
            Rights: this.state.newNote.rights !==  this.state.collectionNotes.holds.includes('Rights'),
            Cover: this.state.newNote.cover !==  this.state.collectionNotes.holds.includes('Cover'),
            Other: this.state.newNote.other !==  this.state.collectionNotes.holds.includes('Other')
        };

        Object.entries(changeStatus).forEach(([holdType, hasChanged]) => {
            if (hasChanged) {
                collectionNotes.collectionNoteHolds.push(
                    {
                        hold: holdType,
                        selected : this.state.newNote[holdType.toLowerCase()],
                        collectionDTO :  this.state.collectionNotes &&  this.state.collectionNotes.length > 0 ?  this.state.collectionNotes[0].collection : this.props.collection,
                    }
                );
            }
        });

        AxiosClient.post(url,collectionNotes)
            .then(() => {
                this.fetchNotes();
            })
            .then(() => this.hideNoteModal())
            .catch(error => {
                console.log("Error ", error);
            });
    }


    render() {
        const isCollectionReady = this.props.collection !== {};
        const collId = this.props.match.params.collId;
        const rootPath = '/print-book/' + collId + '/';
        const pcsLogPath = `${rootPath}pcs-log`;
        const userRolePermissions = this.props.permissions;
        const bookDetailsPageRoutes = [
            {
                path: rootPath.concat('book-details'),
                component: BookDetails,
                title: 'Book Details',
                options: {
                    changeCollectionTitle: this.changeCollectionTitle,
                    changePrintColorSetting: this.changePrintColorSetting,
                    changePrintTOCChapterType: this.changePrintTOCChapterType,
                    changeAutomaticPageNumbering: this.changeAutomaticPageNumbering,
                    changeAutomaticIndexes: this.changeAutomaticIndexes,
                    changeForcedRectoStart: this.changeForcedRectoStart,
                    changePaperWeight: this.changePaperWeight,
                    changeSelectedBindingType: this.changeSelectedBindingType,
                    changeSelectedPaperType: this.changeSelectedPaperType,
                    bookDetails: this.state.bookDetails,
                    selectedBindingType: this.state.selectedBindingType,
                    selectedPaperType: this.state.selectedPaperType,
                    bindingTypes: this.state.bindingTypes,
                    paperTypes: this.state.paperTypes,
                    syncPageCount: this.syncPageCount,
                    collectionNotes: this.state.collectionNotes,
                    fetchNotes: this.fetchNotes,
                    showNoteModal: this.showNoteModal,
                    hideNoteModal: this.hideNoteModal,
                    loadCurrentHoldsStatus: this.loadCurrentHoldsStatus,
                    permissions:this.props.permissions
                }
            },
            {
                path: rootPath.concat('order-details'),
                component: OrderDetails,
                title: 'Order Details',
                options: {
                    deskCopies: this.state.deskCopies,
                    updateDeskCopy: this.updateDeskCopy,
                    permissions:this.props.permissions
                }
            },
            {
                path: rootPath.concat('bundles'),
                component: BundleInfo,
                title: 'Bundle Info',
                options: {
                    bundleInfo: this.state.bundleInfo,
                    setBundleInfo: this.setBundleInfo,
                    permissions:this.props.permissions
                }
            },
            {
                path: rootPath.concat('components'),
                component: BookComponents,
                title: 'Components',
                options: {
                    pdfs: this.state.frontMatterFilteredPdfs,
                    collectionId: this.props.match.params.collId,
                    getPdfLinks: this.getPdfLinks,
                    drvs: this.drvs,
                    printAdminComponent: this.printAdminComponent,
                    getDRVS: this.getDRVS,
                    permissions:this.props.permissions
                }
            },
            {
                path: rootPath.concat('cover'),
                component: CoverTitle,
                title: 'Cover & Title',
                options: {
                    setCoverAndTitlePageState: this.setCoverAndTitlePageState,
                    changeBookTitle: this.changeBookTitle,
                    changeBookSubtitle: this.changeBookSubtitle,
                    changeBookAuthor: this.changeBookAuthor,
                    changeBookOther: this.changeBookOther,
                    permissions:this.props.permissions

                }
            }
        ];

        const courseInformation = this.props.collection.collectionCourseInformation ? this.props.collection.collectionCourseInformation : {};
        const firstNote = (this.state.collectionNotes
            && this.state.collectionNotes.collectionNoteDTOS
            && this.state.collectionNotes.collectionNoteDTOS.length > 0
            && this.state.collectionNotes.collectionNoteDTOS[0])
            ? this.state.collectionNotes.collectionNoteDTOS[0]
            : null;

        return (
            isCollectionReady && <div className="book-container">
                {this.props.location.pathname.includes("/book-details") && <div className="pe-row">
                    <CollectionAlerts
                        collection={this.props.collection}
                        resources={this.props.collectionChapterResources}
                        printAdminComponent={this.props.printAdminComponent}
                        notes={this.state.collectionNotes ? this.state.collectionNotes.collectionNoteDTOS : []} />
                </div>}
                <div className="pe-row">
                    <div className="pe-col-12">
                        <label className="pe-page-title--small">{this.props.collection.title}</label>
                    </div>
                </div>
                <div className="info-row pe-row">
                    <div className="pe-col-2">
                        <p><strong>ISBN: </strong>{this.props.collection.isbn}</p>
                        <p className="isbn13">{this.props.collection.isbn13}</p>
                    </div>
                    <div className="pe-col-2">
                        <p><strong>Package ISBN: </strong>{this.props.bookBuildDetails.bundleISBN}</p>
                        <p className="access-isbn13">{this.props.bookBuildDetails.bundleISBN13}</p>
                    </div>
                    <div className="pe-col-3">
                        <p><strong>Estimated Enrollments: </strong>{courseInformation.estimatedQty}</p>
                        <p><strong>Scrubbed/Unscrubbed: </strong>{this.props.collection.unscrubbed === false ? 'Scrubbed' : 'Unscrubbed'}</p>
                    </div>
                    {this.state.collectionNotes && <div className="pe-col-5">
                        {this.state.collectionNotes.holds.map(hold => <span class="tableBox orangeBox note-summery-field">{hold}</span>)}
                        {firstNote && <p className="note-summary note-summery-field">
                            {`${moment.utc(firstNote.createdTimestamp).local().format("MM-DD-YYYY HH:mm:ss")} (${firstNote.body})`}
                        </p>}
                    </div>}
                </div>  
                <div className="pe-row display-flex">
                    <div className="pe-col-2 left-wrapper">
                        <BookNavigationPanel match={this.props.match} pageRoutes={bookDetailsPageRoutes}/>
                        <div className="book-build-button-wrapper">
                            {
                                (this.props.guardDirtyIsActive && this.state.isChangesValid && !userRolePermissions.includes(PERMISSIONS.view_only_save_changes)) ?  <button onClick={this.saveChanges} className="pe-btn__primary--btn_small">Save Changes</button>: <button onClick={this.saveChanges} disabled className="pe-btn__primary--btn_small">Save Changes</button>
                            }
                            <br/>
                            <br/>
                            {(this.props.collection.adminProcessingStatus !== "Canceled" && this.props.resourceSharing && !userRolePermissions.includes(PERMISSIONS.view_only_rebuild_book,)) ?
                                <button onClick={this.rebuildBook} className="send-to-pcs pe-btn__primary--btn_small">Rebuild Book</button> :
                                <button disabled className="green-button pe-btn__primary--btn_small">Rebuild Book</button>
                            }
                            <br/>
                            <br/>
                            {(this.props.collection.adminProcessingStatus === "Canceled" || userRolePermissions.includes(PERMISSIONS.view_only_send_to_pdh))? <button onClick={this.sendToPCS} className="green-button pe-btn__primary--btn_small" disabled>Send to
                                PDH
                            </button>:<button onClick={this.sendToPCS} className="green-button pe-btn__primary--btn_small" >Send to
                                PDH </button>}
                            <br/>
                            <div className="view-pcs-link">
                                { userRolePermissions.includes(PERMISSIONS.view_only_view_pdh_log) ? <a>View PDH Log</a> : <Link to={pcsLogPath}>View PDH Log</Link>}
                            </div>
                            <br/>
                            {(this.props.collection.adminProcessingStatus === "Canceled" || userRolePermissions.includes(PERMISSIONS.view_only_send_to_pitstop)) ?
                                <button className="green-button pe-btn__primary--btn_small"
                                    onClick={this.sendToPitStop} disabled>Send to Pitstop</button> :
                                <button className="green-button pe-btn__primary--btn_small"
                                    onClick={this.sendToPitStop}>Send to Pitstop</button>
                            }
                            <br/>
                            <br/>
                            {(this.props.collection.adminProcessingStatus === "Canceled" || userRolePermissions.includes(PERMISSIONS.view_only_send_to_printer)) ? 
                                <button className="green-button pe-btn__primary--btn_small"
                                    onClick={this.sendToPrinter} disabled>Send to Printer</button> :
                                <button className="green-button pe-btn__primary--btn_small"
                                    onClick={this.sendToPrinter}>Send to Printer</button>
                            }
                            <br/>
                            <br/>
                            {userRolePermissions.includes(PERMISSIONS.view_only_cancel_order) ? <button className="red-button pe-btn__primary--btn_small"
                                onClick={this.cancelOrder} disabled>Cancel Order</button> : <button className="red-button pe-btn__primary--btn_small" onClick={this.cancelOrder}>Cancel Order</button>}
                            <br/>

                        </div>
                        {(this.props.collection.collectionId) && <PdfLink getDRVS={this.props.getDRVS} totalPdfs={this.state.totalPdfs} pdfs={this.state.pdfs} getPdfs={this.getPdfLinks} collectionId={this.props.match.params.collId} handlePitStopStatus={this.handlePitStopStatus} permissions= {this.props.permissions}/>}
                    </div>
                    <div className="pe-col-10 right-wrapper">
                        <Switch>
                            {bookDetailsPageRoutes.map((route) => (<Route key={route.path} path={route.path}
                                render={() => <route.component {...this.props}
                                    setComponent={this.setActiveComponent}
                                    options={route.options}/>}/>))}
                            <Route path={pcsLogPath}
                                render={() => <PCSLogComponent logs={this.state.pcsLogs} getPCSLogs={() => {
                                    this.fetchPCSLogs();
                                }}/>}/>
                            <Redirect to={rootPath + 'book-details'}/>
                        </Switch>
                    </div>
                </div>
                <Prompt
                    when={this.props.guardDirtyIsActive}
                    message="Changes you made may not be saved / There is unsaved data. Are you sure you want to leave this page?"
                />
                {this.props.children}
                {this.state.collectionNotes && <ModalDialog
                    showModal={this.state.isNotesModalActive}
                    onSuccess={this.saveNotesWithHolds}
                    onCancel={this.hideNoteModal}
                    title="Print Collection Notes">
                    <AddNoteModal
                        notes={this.state.collectionNotes.collectionNoteDTOS}
                        holds={this.state.collectionNotes.holds}
                        newNote={this.state.newNote}
                        toggleHold={this.toggleHold}
                        updateNote={this.updateNote}
                    />
                </ModalDialog>}
            </div>
        );
    }

}

const BookContainer = props => (
    <AuthConsumer>
        {({userProfile}) => (
            <AlertConsumer>
                {({ addAlert }) => (
                    <CollectionProvider collectionId={props.match.params.collId}>
                        <CollectionConsumer>
                            {({collection, bookBuildDetails, resourceData, drvs, getDRVS, printAdminComponent, loadInitialData, updateCollection, updateBookBuildDetails, resourceSharing, loadBookBuildDetails}) => {
                                return (
                                    <BookContainerWithContext
                                        guardDirtyIsActive={props.guardDirtyIsActive}
                                        resourceSharing={resourceSharing}
                                        guardDirtySetActive={props.guardDirtySetActive}
                                        match={props.match}
                                        location={props.location}
                                        collection={collection}
                                        bookBuildDetails={bookBuildDetails}
                                        loadInitialData={loadInitialData}
                                        updateCollection={updateCollection}
                                        updateBookBuildDetails={updateBookBuildDetails}
                                        collectionChapterResources={resourceData}
                                        loadCollection={loadInitialData}
                                        userId={userProfile ? userProfile.id : ""}
                                        drvs={drvs}
                                        printAdminComponent={printAdminComponent}
                                        getDRVS= {getDRVS}
                                        addAlert={addAlert}
                                        loadBookBuildDetails={loadBookBuildDetails}
                                        permissions={Array.isArray(userProfile.permissions)?userProfile.permissions:[]}
                                    />
                                );
                            }
                            }
                        </CollectionConsumer>
                    </CollectionProvider>
                )}
            </AlertConsumer>
        )}
    </AuthConsumer>
);


export default guardDirtyState() (BookContainer);



// WEBPACK FOOTER //
// ./src/components/pages/BookContainer/BookContainer.js